import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useLocations } from '../api';

interface LocationSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> {
  siteId: string | undefined;
  value: string;
  onChange: (locationId: string, label: string) => void;
}

/** Site-scoped location picker. Unlike products (a potentially large catalog,
 * hence `ProductSelect`'s server-side typeahead), a site's locations are a short,
 * fully-enumerable list (yard/silos/warehouse/bins/stores — BUILD_SPEC.md §5.2), so
 * this fetches the whole (active) set for the chosen site once and filters
 * client-side via Mantine's built-in `searchable` matching. Disabled until a site is
 * chosen, since every location belongs to exactly one site. */
export function LocationSelect({ siteId, value, onChange, ...rest }: LocationSelectProps) {
  const { data, isLoading } = useLocations(siteId);

  const options = useMemo(
    () => (data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
    [data],
  );

  return (
    <Select
      {...rest}
      searchable
      data={options}
      value={value || null}
      disabled={!siteId || rest.disabled}
      placeholder={!siteId ? 'Choose a site first' : (rest.placeholder ?? 'Choose a location…')}
      description={rest.description ?? 'The physical spot within the site — yard, silo, warehouse, bin or store'}
      onChange={(newValue) => {
        const match = newValue ? options.find((o) => o.value === newValue) : undefined;
        onChange(newValue ?? '', match?.label ?? '');
      }}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No locations found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
